import { Exclude } from "class-transformer";
import { EmployeeStatus, EmploymentType, EntityFilter, TimeEntryType, TimeFilter } from "./model";
import { DateRange } from "./time";
import {
    parseTimes,
    getAllContractsForRange,
    getContractForDate,
    findEmployeeById,
    findEmployeeByStaffNumber,
    getVenue,
    getDepartment,
    findPositionById,
} from "./utils";
import { DateString } from "@pentacode/openapi";

export type FilterContextPosition = { id: number };
export type FilterContextDepartment = { id: number; positions: FilterContextPosition[] };
export type FilterContextVenue = { id: number; departments: FilterContextDepartment[] };
export type FilterContextTimeEntry = {
    employeeId: number | null;
    positionId: number | null;
    position: FilterContextPosition | null;
    date?: string | DateRange;
};

export type FilterTypeSalary = {
    accountingSalaryConfigId: number | null;
    positionId: number | null;
};
export type FilterTypeContract = {
    employmentType: EmploymentType;
    salaries: FilterTypeSalary[];
    start: DateString;
    end: DateString | null;
};

export type FilterContextEmployee = {
    staffNumber: number;
    id: number;
    status: EmployeeStatus;
    positions: FilterContextPosition[];
    currentContract: FilterTypeContract | undefined | null;
    latestContract: FilterTypeContract | undefined | null;
    tags: { id: number }[];
    contracts: FilterTypeContract[];
};

type ContextCompanyPosition = {
    id: number;
};
type ContextCompanyDepartment = {
    id: number;
    positions: ContextCompanyPosition[];
    archivedPositions: ContextCompanyPosition[];
};
type ContextCompanyVenue = {
    id: number;
    departments: ContextCompanyDepartment[];
    archivedDepartments: ContextCompanyDepartment[];
};
export type FilterContextCompany = {
    id: number;
    venues: ContextCompanyVenue[];
    archivedVenues: ContextCompanyVenue[];
    employees: FilterContextEmployee[];
    costCenters: { number: string; entities: EntityFilter[] }[];
};

export interface EntityFilterContext {
    company: FilterContextCompany;
    employee?: FilterContextEmployee;
    date?: string | DateRange;
    venue?: FilterContextVenue | null;
    department?: FilterContextDepartment | null;
    position?: FilterContextPosition | number | null;
    timeEntry?: FilterContextTimeEntry;
}

export class EmployeeFilters {
    constructor(init: Partial<EmployeeFilters> = {}) {
        Object.assign(this, init);
    }

    filters: EntityFilter[] = [{ type: "employeeStatus", value: "active" as EmployeeStatus }];

    sortProperty: EmployeeSortProperty = "lastName";

    sortDirection: EmployeeSortDirection = "ascending";

    @Exclude()
    text: string = "";
}

export function matchesEmploymentType(
    employee: FilterContextEmployee,
    type: EmploymentType,
    date?: string | DateRange
): boolean {
    const contract = !date
        ? employee.currentContract
        : typeof date === "string"
          ? getContractForDate(employee.contracts || [], date as DateString)
          : getAllContractsForRange(employee.contracts || [], date);

    if (Array.isArray(contract)) {
        return contract.some((c) => c.employmentType === type);
    }

    return contract?.employmentType === type;
}

export function matchesFilter(
    filter: EntityFilter,
    { company, employee, date, venue, department, position, timeEntry }: EntityFilterContext
): boolean {
    if (timeEntry?.positionId && !position) {
        position = timeEntry.positionId;
    }

    if (timeEntry?.position && !position) {
        position = timeEntry.position;
    }

    if (typeof position === "number") {
        position = findPositionById([...company.venues, ...company.archivedVenues], position)?.position;
    }

    if (timeEntry?.employeeId && !employee) {
        employee = findEmployeeById(company.employees, timeEntry.employeeId);
    }

    if (timeEntry && !date) {
        date = timeEntry.date;
    }

    if (["employeeId", "employeeTag", "staffNumber"].includes(filter.type) && timeEntry && !employee) {
        return false;
    }

    const positions = getPositionsByFilter(filter, company);

    switch (filter.type) {
        case "venue":
        case "department":
        case "position":
            return (
                (!venue ||
                    venue.departments.some((dep) =>
                        dep.positions.some((pos) => positions?.some((p) => p.id === pos.id))
                    )) &&
                (!department || department.positions.some((pos) => positions?.some((p) => p.id === pos.id))) &&
                (!position || positions?.some((p) => p.id === (position as FilterContextPosition).id)) &&
                (!employee || employee.positions.some((pos) => positions?.some((p) => p.id === pos.id)))
            );
        case "employmentType":
            if (!employee) {
                return false;
            }
            return matchesEmploymentType(employee, filter.value, date);
        case "employeeStatus":
            if (!employee) {
                return false;
            }
            return employee.status === filter.value;
        case "employeeId":
            return (
                (!venue ||
                    venue.departments.some((dep) =>
                        dep.positions.some((pos) => positions?.some((p) => p.id === pos.id))
                    )) &&
                (!department || department.positions.some((pos) => positions?.some((p) => p.id === pos.id))) &&
                (!position || positions?.some((p) => p.id === (position as FilterContextPosition).id)) &&
                (!employee || employee.id === filter.value)
            );
        case "staffNumber":
            return (
                (!venue ||
                    venue.departments.some((dep) =>
                        dep.positions.some((pos) => positions?.some((p) => p.id === pos.id))
                    )) &&
                (!department || department.positions.some((pos) => positions?.some((p) => p.id === pos.id))) &&
                (!position || positions?.some((p) => p.id === (position as FilterContextPosition).id)) &&
                (!employee || employee.staffNumber === filter.value)
            );
        case "employeeTag":
            if (!employee) {
                return false;
            }
            return employee.tags?.some((tag) => tag.id === filter.value);
        case "costCenter": {
            const constCenter = company.costCenters.find((cc) => cc.number === filter.value);
            if (!constCenter) {
                return false;
            }
            return matchesFilters(constCenter.entities, {
                company,
                employee,
                date,
                venue,
                department,
                position,
            });
        }
    }
}

export function groupByFilterType(filters: EntityFilter[]) {
    const filterTypes: {
        workspace: EntityFilter[];
        employee: EntityFilter[];
        employmentType: EntityFilter[];
        employeeStatus: EntityFilter[];
        employeeTag: EntityFilter[];
        costCenter: EntityFilter[];
    } = {
        workspace: [],
        employee: [],
        employmentType: [],
        employeeStatus: [],
        employeeTag: [],
        costCenter: [],
    };

    for (const filter of filters) {
        switch (filter.type) {
            case "venue":
            case "department":
            case "position":
                filterTypes.workspace.push(filter);
                break;
            case "employeeId":
            case "staffNumber":
                filterTypes.employee.push(filter);
                break;
            case "employmentType":
                filterTypes.employmentType.push(filter);
                break;
            case "employeeStatus":
                filterTypes.employeeStatus.push(filter);
                break;
            case "employeeTag":
                filterTypes.employeeTag.push(filter);
                break;
            case "costCenter":
                filterTypes.costCenter.push(filter);
                break;
        }
    }

    return filterTypes;
}

export function matchesFilters(filters: EntityFilter[], context: EntityFilterContext) {
    const filterTypes = groupByFilterType(filters);

    return [...Object.values(filterTypes)].every(
        (filters) => !filters.length || filters.some((filter) => matchesFilter(filter, context))
    );
}

export function matchesWorkspaceFilters(
    filters: EntityFilter[],
    context: Pick<EntityFilterContext, "company" | "venue" | "department" | "position">
) {
    const workspaceFilters = groupByFilterType(filters).workspace;

    return !workspaceFilters.length || workspaceFilters.some((filter) => matchesFilter(filter, context));
}

export function getSpecificity(filters: EntityFilter[]) {
    const types = new Set(filters.map((f) => f.type));
    let score = types.size;
    if (types.has("employeeId")) {
        score += 10;
    }
    return score;
}

export function serializeFilters(filters: EntityFilter[]) {
    return btoa(JSON.stringify(filters)).replace(/=/g, "");
}

export function deserializeFilters(filters: string) {
    return JSON.parse(atob(filters)) as EntityFilter[];
}

export type EmployeeSortProperty =
    | "firstName"
    | "lastName"
    | "staffNumber"
    | "birthday"
    | "email"
    | "birthdayDate"
    | "contractEnd"
    | "employedDays";

export type EmployeeSortDirection = "ascending" | "descending";

/**
 * Optimizes sorting by properties that might have complicated calculations
 * by pre-calculating the sort properties and sorting by them.
 * The tradeoff is that we instantiate 3 new arrays and 2 new functions and NOT mutating the input array in place.
 */
export function sortBy<TEntry, TSortProp extends string | number>(
    arr: TEntry[],
    fnBy: (val: TEntry, index: number) => TSortProp,
    sortDirection: "ascending" | "descending"
): TEntry[] {
    // Pre-calculate sort properties once
    const sortProps: (string | number)[] = new Array(arr.length);
    const indices = new Array(arr.length);
    for (let i = 0; i < arr.length; i++) {
        sortProps[i] = fnBy(arr[i], i);
        indices[i] = i;
    }

    // sort indices by sort properties
    indices.sort((indexA, indexB) => {
        const aVal = sortProps[indexA];
        const bVal = sortProps[indexB];
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    });

    // create new array with sorted entries
    const ascending = sortDirection === "ascending";
    const result: TEntry[] = new Array(arr.length);
    for (let i = 0; i < arr.length; i++) {
        result[i] = ascending ? arr[indices[i]] : arr[indices[arr.length - i - 1]];
    }

    return result;
}

function getPositionsByFilter(filter: EntityFilter, company: FilterContextCompany): FilterContextPosition[] {
    switch (filter.type) {
        case "venue":
            return (
                getVenue([...company.venues, ...company.archivedVenues], filter.value)?.departments.flatMap(
                    (department) => department.positions
                ) ?? []
            );
        case "department":
            return (
                getDepartment([...company.venues, ...company.archivedVenues], filter.value).department?.positions ?? []
            );
        case "position": {
            const position = findPositionById([...company.venues, ...company.archivedVenues], filter.value)?.position;
            return position ? [position] : [];
        }
        case "employeeId":
            return findEmployeeById(company.employees, filter.value)?.positions ?? [];
        case "staffNumber":
            return findEmployeeByStaffNumber(company.employees, filter.value)?.positions ?? [];
        case "employeeStatus":
        case "employmentType":
        case "employeeTag":
        case "costCenter":
            return [];
    }
}

export function matchesTimeFilter(
    {
        date,
        start,
        end,
        type,
    }: { date: DateString; type: TimeEntryType; start: Date | string | null; end: Date | string | null },
    { from, to, fromRule, toRule }: TimeFilter
) {
    if (type !== TimeEntryType.Work) {
        return true;
    }

    if (typeof start === "string") {
        start = new Date(start);
    }

    if (typeof end === "string") {
        end = new Date(end);
    }

    const [filterStart, filterEnd] = parseTimes(date, from, to);

    let startOk = false;
    let endOk = false;

    switch (fromRule) {
        case "equal":
            startOk = !filterStart || (!!start && filterStart.getTime() === start.getTime());
            break;
        case "open":
            // Does it end before our filter starts (within or later)
            startOk = !filterStart || !end || end > filterStart;
            break;
        default:
            startOk = !filterStart || ((!start || start >= filterStart) && (!end || end > filterStart));
            break;
    }

    switch (toRule) {
        case "equal":
            endOk = !filterEnd || (!!end && filterEnd.getTime() === end.getTime());
            break;
        case "open":
            // does it start before our filter ends (within or earlier)
            endOk = !filterEnd || !start || start < filterEnd;
            break;
        default:
            endOk = !filterEnd || ((!end || end <= filterEnd) && (!start || start < filterEnd));
            break;
    }

    return startOk && endOk;
}
